import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import RecurringWorkspaceItemConstants from 'Shared/constants/RecurringWorkspaceItem.constants';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import RecurringWorkspaceItemHelper from 'Shared/model_helpers/RecurringWorkspaceItem.helper';
import { NEW_TIME_OF_DAY_DEFAULT } from '../utils';

// Actions
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import CustomSingleDatePicker from 'Shared/components/CustomSingleDatePicker/CustomSingleDatePicker';

// Styles
import './SnoozeModal.scss';

type Props = {
  isOpen: boolean;
  toggleModal(): void;
  meetingItem: Record<MeetingItem>;
};

const SnoozeModal = ({ isOpen, toggleModal, meetingItem }: Props) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);

  const startsAt = RecurringWorkspaceItemHelper.getStartsAt(recurringWorkspaceItem);
  const onlyRecurOnce = RecurringWorkspaceItemHelper.getOnlyRecurOnce(recurringWorkspaceItem);

  const [isDeleteing, toggleIsDeleting] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);

  const [tempStartsAt, setTempStartsAt] = useState(
    startsAt && onlyRecurOnce ? moment(startsAt) : moment().add(1, 'week')
  );

  const handleSaveRecurrenceClick = useCallback(() => {
    toggleIsSaving();
    const data = {
      [RecurringWorkspaceItemConstants.Model.DAY_OF_WEEK]: moment(tempStartsAt).format('dddd'),
      [RecurringWorkspaceItemConstants.Model.INTERVAL_AMOUNT]: 1,
      [RecurringWorkspaceItemConstants.Model.INTERVAL_UNIT]: 'week',
      [RecurringWorkspaceItemConstants.Model.TIME_OF_DAY]: NEW_TIME_OF_DAY_DEFAULT,
      [RecurringWorkspaceItemConstants.Model.STARTS_AT]: moment(tempStartsAt).toDate(),
      [RecurringWorkspaceItemConstants.Model.ONLY_RECUR_ONCE]: true,
      section_rule_action: 'disconnect',
    };
    dispatch(MeetingItemActions.updateRecurringItem(uuid, data, 'Item snoozed')).then(() => {
      toggleIsSaving();
      toggleModal();
      const itemCompletionData = {
        item: {
          completed: true,
        },
        [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
      };
      dispatch(MeetingItemActions.updateCompleteStatus(uuid, itemCompletionData));
    });
  }, [toggleIsSaving, tempStartsAt]);

  const handleRemoveClick = useCallback(() => {
    toggleIsDeleting();
    dispatch(MeetingItemActions.deleteRecurrenceItem(uuid)).then(() => {
      toggleIsDeleting();
      toggleModal();
    });
  }, [toggleIsDeleting]);

  const showDeleteButton = recurringWorkspaceItem && onlyRecurOnce;
  const buttonsDisabled = isDeleteing || isSaving;

  if (!isOpen) return null;

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
      <WpModalHeader>{tr('perform.meeting_item.revisit_dropdown.snooze_until_specific_date')}</WpModalHeader>
      <WpModalBody>
        {tr('perform.meeting_item.snooze_modal.description')}
        <div className="SnoozeModal__editor">
          <form>
            <div className="d-flex align-items-center mb-2">
              <span className="start-date-label mr-1">{tr('perform.meeting_item.snooze_modal.revisit_on')}</span>
              <CustomSingleDatePicker date={tempStartsAt} onDateChange={setTempStartsAt} withPortal />
            </div>
          </form>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <div className="SnoozeModal__footer">
          {showDeleteButton && (
            <WpButton variant="tertiary" onClick={handleRemoveClick} loading={isDeleteing} disabled={buttonsDisabled}>
              {tr('perform.meeting_item.snooze_modal.remove_snooze')}
            </WpButton>
          )}
          <div className="ml-auto">
            <WpButtonGroup>
              <WpButton variant="tertiary" onClick={() => toggleModal()} disabled={buttonsDisabled}>
                {tr('perform.meeting_item.snooze_modal.cancel')}
              </WpButton>
              <WpButton onClick={handleSaveRecurrenceClick} disabled={buttonsDisabled} loading={isSaving}>
                {tr('perform.meeting_item.snooze_modal.save')}
              </WpButton>
            </WpButtonGroup>
          </div>
        </div>
      </WpModalFooter>
    </WpModal>
  );
};

export default SnoozeModal;
